/**
 * Product editor — `/catalog/products/:productId` and `/catalog/products/new`
 * (SPEC screen 2). Tabs: Details | Variants | Barcodes | Components.
 * One ETag-guarded Save covers Details + the variant grid; barcode and
 * component mutations are immediate row actions with their own semantics.
 * The variant-grid columns derive from the product's ATTRIBUTE ASSIGNMENTS
 * (GET /products/{id}/attributes joined to the definitions, position order);
 * the Variants tab carries the assign/unassign admin panel, so columns react
 * to every assignment mutation via the invalidated assignments query.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
} from "@erp/ui";

import {
  ConflictDialog,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormSubmit,
  useZodForm,
} from "@/components/form";
import {
  claimIdempotencyAttempt,
  isAmbiguousIdempotencyFailure,
  isIdempotencyKeyReuse,
  type IdempotencyAttempt,
} from "@/features/shared/ops-api";
import { ApiError } from "@/lib/api/errors";

import {
  PRODUCT_STATUSES,
  PRODUCT_TYPES,
  createProduct,
  lookupVariantsByBarcode,
  updateProduct,
  type Attribute,
  type ProductCreateInput,
  type ProductDetail,
  type ProductInput,
} from "../api";
import {
  catalogKeys,
  useActiveUoms,
  useAttributes,
  useCategories,
  useProductAttributes,
  useProductComponents,
  useProductDetail,
  useTaxClasses,
  useUoms,
  useVariantBarcodeCounts,
  useVariantBarcodes,
  useVariantOptions,
  type ProductDetailSnapshot,
} from "../queries";
import { buildEditorSchema, compactAttributeValues, type EditorValues } from "../variant-schema";
import { AttributesPanel } from "../components/attributes-panel";
import { BarcodesPanel } from "../components/barcodes-panel";
import { ComponentsPanel } from "../components/components-panel";
import { VariantGrid } from "../components/variant-grid";

export type EditorTab = "details" | "variants" | "barcodes" | "components";

export interface ProductEditorPageProps {
  /** Undefined for `/catalog/products/new`. */
  productId?: string;
  /** `?variant=` deep link — opens the Variants tab on that row. */
  initialVariantId?: string;
  /** Permission-guarded zero-hit handoff — opens Barcodes with this value prefilled. */
  initialBarcode?: string;
  /** After a successful create, navigate to the saved product's editor. */
  onCreated: (productId: string) => void;
  /** Open another product's editor (barcode owner links). */
  onOpenProduct: (productId: string, variantId?: string) => void;
  /** Cycle-loop SKU link → catalog list filtered to that SKU. */
  onOpenSku: (sku: string) => void;
  /** `uom_unconvertible` → UoM admin pre-filled `?from=&to=`. */
  onOpenUomPair: (fromUom: string | undefined, toUom: string | undefined) => void;
  canSave?: boolean;
  canReadAttributes?: boolean;
  canManageAttributes?: boolean;
  canReadBarcodes?: boolean;
  canCreateBarcode?: boolean;
  canDeleteBarcode?: boolean;
  canReadComponents?: boolean;
  canCreateComponent?: boolean;
  canDeleteComponent?: boolean;
  canReadUoms?: boolean;
  canReadCategories?: boolean;
  canReadTaxClasses?: boolean;
  canReadVariants?: boolean;
}

const EMPTY_VALUES: EditorValues = {
  name: "",
  description: "",
  product_type: "goods",
  // Create's `status` is optional in the contract; the UI defaults to active.
  status: "active",
  tax_class_id: "",
  base_uom_id: "",
  category_id: "",
  variants: [],
};

function toFormValues(detail: ProductDetail): EditorValues {
  return {
    name: detail.name,
    description: detail.description,
    product_type: detail.product_type,
    status: detail.status,
    tax_class_id: detail.tax_class_id ?? "",
    base_uom_id: detail.base_uom_id,
    category_id: detail.category_id ?? "",
    variants: (detail.variants ?? []).map((variant) => ({
      variantId: variant.id,
      sku: variant.sku,
      status: variant.status,
      attribute_values: { ...variant.attribute_values },
    })),
  };
}

type EditorPayload = ProductInput & ProductCreateInput;

function toPayload(values: EditorValues): EditorPayload {
  return {
    name: values.name,
    description: values.description,
    product_type: values.product_type,
    status: values.status,
    tax_class_id: values.tax_class_id,
    base_uom_id: values.base_uom_id,
    category_id: values.category_id,
    variants: values.variants.map((variant) => ({
      ...(variant.variantId === "" ? {} : { id: variant.variantId }),
      sku: variant.sku,
      status: variant.status,
      attribute_values: compactAttributeValues(variant.attribute_values),
    })),
  };
}

export function ProductEditorPage({
  productId,
  initialVariantId,
  initialBarcode,
  onCreated,
  onOpenProduct,
  onOpenSku,
  onOpenUomPair,
  canSave = true,
  canReadAttributes = true,
  canManageAttributes = true,
  canReadBarcodes = true,
  canCreateBarcode = true,
  canDeleteBarcode = true,
  canReadComponents = true,
  canCreateComponent = true,
  canDeleteComponent = true,
  canReadUoms = true,
  canReadCategories = true,
  canReadTaxClasses = true,
  canReadVariants = true,
}: ProductEditorPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isNew = productId === undefined;

  const [tab, setTab] = useState<EditorTab>(
    initialBarcode !== undefined && canReadBarcodes
      ? "barcodes"
      : initialVariantId !== undefined
        ? "variants"
        : "details",
  );
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    initialVariantId ?? null,
  );

  const detailQuery = useProductDetail(productId);
  const detailSnapshot = detailQuery.data;
  const [acceptedDetail, setAcceptedDetail] = useState<{
    productId: string;
    snapshot: ProductDetailSnapshot;
  } | null>(null);
  const acceptedSnapshot =
    acceptedDetail !== null && acceptedDetail.productId === productId
      ? acceptedDetail.snapshot
      : undefined;
  const detail = acceptedSnapshot?.data;

  // Grid columns = the product's assignment set (position order) joined to
  // the global definitions for code/type/options.
  const needsAttributeClosure = !isNew && canReadAttributes;
  const definitionsQuery = useAttributes(needsAttributeClosure);
  const assignmentsQuery = useProductAttributes(productId, needsAttributeClosure);
  const attributes = useMemo(() => {
    // Disabled queries may still expose privileged cache data. Opaque mode
    // must neither render nor validate definitions the user cannot read.
    if (!canReadAttributes) return [];
    const definitions = new Map(
      (definitionsQuery.data ?? []).map((definition) => [definition.id, definition]),
    );
    return (assignmentsQuery.data ?? [])
      .slice()
      .sort((a, b) => a.position - b.position || a.attribute_id.localeCompare(b.attribute_id))
      .map((assignment) => definitions.get(assignment.attribute_id))
      .filter((definition): definition is Attribute => definition !== undefined);
  }, [canReadAttributes, definitionsQuery.data, assignmentsQuery.data]);
  const attributeQueriesSettled =
    assignmentsQuery.isSuccess &&
    definitionsQuery.isSuccess &&
    !assignmentsQuery.isFetching &&
    !definitionsQuery.isFetching &&
    !assignmentsQuery.isRefetchError &&
    !definitionsQuery.isRefetchError;
  const hasCompleteAttributeClosure =
    attributeQueriesSettled &&
    assignmentsQuery.data.every((assignment) =>
      definitionsQuery.data.some((definition) => definition.id === assignment.attribute_id),
    );
  const attributeClosureReady = !needsAttributeClosure || hasCompleteAttributeClosure;
  const attributeClosureFailed =
    needsAttributeClosure &&
    (assignmentsQuery.isError ||
      definitionsQuery.isError ||
      assignmentsQuery.isRefetchError ||
      definitionsQuery.isRefetchError ||
      (attributeQueriesSettled && !hasCompleteAttributeClosure));
  const attributeClosureLoading =
    needsAttributeClosure && !attributeClosureReady && !attributeClosureFailed;

  const taxClasses = useTaxClasses(canReadTaxClasses).data ?? [];
  const uoms = useUoms(canReadUoms).data ?? [];
  const activeUomsQuery = useActiveUoms(canReadUoms);
  const activeUoms = activeUomsQuery.data ?? [];
  const categories = useCategories(canReadCategories).data ?? [];
  const variantOptions = useVariantOptions("", canReadVariants && canReadComponents).data ?? [];

  const variantIds = useMemo(
    () => (detail?.variants ?? []).map((variant) => variant.id),
    [detail?.variants],
  );
  const barcodeCounts = useVariantBarcodeCounts(variantIds, canReadBarcodes).data ?? {};

  const activeVariantId = selectedVariantId ?? detail?.variants?.[0]?.id ?? null;
  const variantBarcodes =
    useVariantBarcodes(isNew ? null : activeVariantId, canReadBarcodes).data ?? [];

  const componentsQuery = useProductComponents(productId, canReadComponents);
  const components = componentsQuery.data ?? [];

  const schema = useMemo(() => buildEditorSchema(attributes), [attributes]);
  const form = useZodForm(schema, { defaultValues: EMPTY_VALUES });
  const isFormDirty = form.formState.isDirty;

  const acceptDetailSnapshot = useCallback(
    (snapshot: ProductDetailSnapshot) => {
      if (productId === undefined) return;
      setAcceptedDetail({ productId, snapshot });
      form.reset(toFormValues(snapshot.data));
    },
    [form, productId],
  );

  // Hydrate only an accepted server snapshot. A background refetch must never
  // erase local edits or advance their If-Match validator behind the user's
  // back: retain the old ETag so a stale save gets the canonical 412.
  useEffect(() => {
    if (detailSnapshot === undefined || productId === undefined) return;
    if (acceptedSnapshot === undefined) {
      acceptDetailSnapshot(detailSnapshot);
      return;
    }
    if (!isFormDirty && detailSnapshot !== acceptedSnapshot) {
      acceptDetailSnapshot(detailSnapshot);
    }
  }, [acceptDetailSnapshot, acceptedSnapshot, detailSnapshot, isFormDirty, productId]);
  const hasDeferredRemoteSnapshot =
    isFormDirty &&
    acceptedSnapshot !== undefined &&
    detailSnapshot !== undefined &&
    detailSnapshot.etag !== acceptedSnapshot.etag;

  // One Idempotency-Key per logical create; replayed on retry, cleared on success.
  const createAttemptRef = useRef<IdempotencyAttempt | null>(null);

  const { onSubmit, isPending, conflict, dismissConflict } = useFormSubmit(
    form,
    async (values: EditorValues) => {
      const payload = toPayload(values);
      if (isNew) {
        const attempt = await claimIdempotencyAttempt(createAttemptRef, payload);
        try {
          const created = await createProduct(payload, attempt.key);
          createAttemptRef.current = null;
          return { kind: "created" as const, id: created.id };
        } catch (error) {
          if (!isAmbiguousIdempotencyFailure(error) && !isIdempotencyKeyReuse(error)) {
            createAttemptRef.current = null;
          }
          throw error;
        }
      }
      const ifMatch = acceptedSnapshot?.etag ?? null;
      if (ifMatch === null) {
        // Every catalog PUT requires If-Match; without a captured ETag the
        // only correct move is a fresh read, never a blind write.
        throw new ApiError("Missing If-Match ETag; reload the product first", 428);
      }
      const { data, etag } = await updateProduct(productId, payload, ifMatch);
      return { kind: "updated" as const, id: productId, data, etag };
    },
    {
      onSuccess: async (result) => {
        toast({ variant: "success", title: t("catalog.editor.saved", "Product saved") });
        if (result.kind === "updated") {
          const snapshot = { data: result.data, etag: result.etag } satisfies ProductDetailSnapshot;
          queryClient.setQueryData(catalogKeys.products.detail(result.id), snapshot);
          acceptDetailSnapshot(snapshot);
        }
        await queryClient.invalidateQueries({ queryKey: catalogKeys.products.all });
        if (result.kind === "created") onCreated(result.id);
      },
      onError: (error) => {
        if (isIdempotencyKeyReuse(error)) {
          toast({
            variant: "destructive",
            title: t(
              "catalog.editor.idempotencyReuse",
              "Duplicate submission with different data — reload before retrying.",
            ),
          });
          return;
        }
        // Field-level errors[] already landed on the form via the kit;
        // only problems without them need a global surface.
        const problem = error instanceof ApiError ? error.problem : undefined;
        if (problem?.errors !== undefined && problem.errors.length > 0) return;
        toast({
          variant: "destructive",
          title: problem?.title ?? t("catalog.editor.saveFailed", "Save failed"),
          description: problem?.detail,
        });
      },
    },
  );

  const submitWhenAttributesReady = (event?: Parameters<typeof onSubmit>[0]) => {
    if (!attributeClosureReady) {
      event?.preventDefault();
      return Promise.resolve();
    }
    return onSubmit(event);
  };

  const reloadAfterConflict = async () => {
    const result = await detailQuery.refetch();
    if (result.isSuccess && result.data !== undefined) {
      acceptDetailSnapshot(result.data);
      dismissConflict();
    }
  };

  const invalidateBarcodes = () => {
    // Covers the per-variant lists AND the count map under catalog/variants.
    void queryClient.invalidateQueries({ queryKey: catalogKeys.variants.all });
  };

  const invalidateComponents = () => {
    void queryClient.invalidateQueries({
      queryKey: catalogKeys.products.components(productId ?? "new"),
    });
  };

  const invalidateAttributes = () => {
    // The variant-grid columns derive from this query — they react in place.
    void queryClient.invalidateQueries({
      queryKey: catalogKeys.products.attributes(productId ?? "new"),
    });
  };

  const typeLabel = (value: string): string => t(`catalog.list.type.${value}`, value);
  const statusLabel = (value: string): string => t(`catalog.list.status.${value}`, value);

  if (!isNew && acceptedSnapshot === undefined && !detailQuery.isError) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isNew && acceptedSnapshot === undefined && detailQuery.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 p-8">
        <p className="text-sm text-muted-foreground">
          {t("catalog.editor.loadError", "The product could not be loaded.")}
        </p>
        <Button variant="outline" onClick={() => void detailQuery.refetch()}>
          {t("actions.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">
            {isNew ? t("catalog.editor.newTitle", "New product") : (detail?.name ?? "")}
          </h1>
          {detail !== undefined ? (
            <Badge variant="secondary">{typeLabel(detail.product_type)}</Badge>
          ) : null}
        </div>
        {/* Save sits at the inline-end of the header (RTL mirrors via flex).
            type=button + onClick: the button lives outside the <form>, and
            jsdom does not submit via the form= attribute association. */}
        {canSave ? (
          <Button
            type="button"
            onClick={() => void submitWhenAttributesReady()}
            loading={isPending || attributeClosureLoading}
            disabled={!attributeClosureReady}
          >
            {t("catalog.editor.save", "Save")}
          </Button>
        ) : null}
      </div>

      {attributeClosureFailed ? (
        <div
          role="alert"
          className="flex max-w-2xl flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3"
        >
          <p className="text-sm text-destructive">
            {t(
              "catalog.editor.attributeClosureError",
              "Product attributes could not be loaded. Saving is disabled to protect variant data.",
            )}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void Promise.all([assignmentsQuery.refetch(), definitionsQuery.refetch()]);
            }}
          >
            {t("actions.retry")}
          </Button>
        </div>
      ) : null}

      {hasDeferredRemoteSnapshot ? (
        <div
          role="status"
          className="flex max-w-2xl flex-wrap items-center justify-between gap-3 rounded-md border border-warning/40 bg-warning/5 px-4 py-3"
        >
          <p className="text-sm text-warning-foreground">
            {t(
              "catalog.editor.remoteUpdate",
              "A newer server version is available. Your unsaved edits are preserved.",
            )}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (detailSnapshot !== undefined) acceptDetailSnapshot(detailSnapshot);
            }}
          >
            {t("catalog.editor.reloadLatest", "Reload latest product")}
          </Button>
        </div>
      ) : null}

      <Tabs value={tab} onValueChange={(next) => setTab(next as EditorTab)}>
        <TabsList>
          <TabsTrigger value="details">{t("catalog.editor.tabs.details", "Details")}</TabsTrigger>
          <TabsTrigger value="variants" disabled={isNew}>
            {t("catalog.editor.tabs.variants", "Variants")}
          </TabsTrigger>
          {canReadBarcodes ? (
            <TabsTrigger value="barcodes" disabled={isNew}>
              {t("catalog.editor.tabs.barcodes", "Barcodes")}
            </TabsTrigger>
          ) : null}
          {canReadComponents ? (
            <TabsTrigger value="components" disabled={isNew}>
              {t("catalog.editor.tabs.components", "Components")}
            </TabsTrigger>
          ) : null}
        </TabsList>

        <Form
          form={form}
          onSubmit={submitWhenAttributesReady}
          id="catalog-product-form"
          className="flex flex-col gap-4"
        >
          <TabsContent value="details" className="mt-4">
            <fieldset disabled={!canSave} className="contents">
              <div className="grid max-w-2xl grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel required>{t("catalog.editor.fields.name", "Name")}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="product_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("catalog.editor.fields.type", "Type")}</FormLabel>
                      {/* key: remount when reset() swaps the value while closed —
                        Radix otherwise re-syncs its hidden native select and
                        emits onValueChange("") (see finance house pattern). */}
                      <Select key={field.value} value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PRODUCT_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {typeLabel(type)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("catalog.editor.fields.status", "Status")}</FormLabel>
                      {/* key: remount when reset() swaps the value while closed —
                        Radix otherwise re-syncs its hidden native select and
                        emits onValueChange("") (see finance house pattern). */}
                      <Select key={field.value} value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PRODUCT_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {statusLabel(status)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="tax_class_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>
                        {t("catalog.editor.fields.taxClass", "Tax class")}
                      </FormLabel>
                      {/* key: remount when reset() swaps the value while closed —
                        Radix otherwise re-syncs its hidden native select and
                        emits onValueChange("") (see finance house pattern). */}
                      <Select key={field.value} value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("catalog.editor.fields.pick", "Pick…")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {taxClasses.map((taxClass) => (
                            <SelectItem key={taxClass.id} value={taxClass.id}>
                              {taxClass.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="base_uom_id"
                  render={({ field }) => {
                    const legacyUom =
                      !isNew && field.value === detail?.base_uom_id
                        ? uoms.find((uom) => uom.id === field.value && uom.status === "archived")
                        : undefined;
                    const choices =
                      legacyUom === undefined ? activeUoms : [legacyUom, ...activeUoms];
                    return (
                      <FormItem>
                        <FormLabel required>
                          {t("catalog.editor.fields.baseUom", "Base UoM")}
                        </FormLabel>
                        {/* key: remount when reset() swaps the value while closed —
                        Radix otherwise re-syncs its hidden native select and
                        emits onValueChange("") (see finance house pattern). */}
                        <Select
                          key={field.value}
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger
                              aria-label={t("catalog.editor.fields.baseUom", "Base UoM")}
                            >
                              <SelectValue placeholder={t("catalog.editor.fields.pick", "Pick…")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {choices.map((uom) => (
                              <SelectItem
                                key={uom.id}
                                value={uom.id}
                                disabled={uom.status === "archived"}
                              >
                                {uom.code} — {uom.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="category_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>
                        {t("catalog.editor.fields.category", "Category")}
                      </FormLabel>
                      {/* key: remount when reset() swaps the value while closed —
                        Radix otherwise re-syncs its hidden native select and
                        emits onValueChange("") (see finance house pattern). */}
                      <Select key={field.value} value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("catalog.editor.fields.pick", "Pick…")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>{t("catalog.editor.fields.description", "Description")}</FormLabel>
                      <FormControl>
                        {/* Native textarea retains platform semantics with the shared input treatment. */}
                        <textarea
                          rows={4}
                          className={cn(
                            "flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs",
                            "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                          )}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </fieldset>
          </TabsContent>

          <TabsContent value="variants" className="mt-4">
            <div className="flex flex-col gap-6">
              <VariantGrid
                attributes={attributes}
                barcodeCounts={barcodeCounts}
                highlightVariantId={selectedVariantId ?? undefined}
                onOpenBarcodes={(variantId) => {
                  setSelectedVariantId(variantId);
                  setTab("barcodes");
                }}
                canEdit={canSave}
                canReadBarcodes={canReadBarcodes}
              />
              {productId !== undefined && canManageAttributes && canReadAttributes ? (
                <AttributesPanel
                  productId={productId}
                  assignments={assignmentsQuery.data ?? []}
                  definitions={definitionsQuery.data ?? []}
                  onChanged={invalidateAttributes}
                  canManage
                />
              ) : null}
            </div>
          </TabsContent>
        </Form>

        <TabsContent value="barcodes" className="mt-4">
          {detail !== undefined ? (
            <BarcodesPanel
              variants={[...(detail.variants ?? [])]}
              barcodes={variantBarcodes}
              selectedVariantId={activeVariantId}
              initialBarcode={initialBarcode}
              onSelectVariant={setSelectedVariantId}
              onChanged={invalidateBarcodes}
              onOpenOwner={(owner, barcode) => {
                if (owner.variant_id !== undefined && canReadVariants) {
                  // Resolve the owning variant's product via the 0-or-1 lookup.
                  void lookupVariantsByBarcode(barcode).then((hits) => {
                    const hit = hits[0];
                    if (hit !== undefined) onOpenProduct(hit.product_id, hit.id);
                    else if (owner.sku !== undefined) onOpenSku(owner.sku);
                  });
                } else if (owner.sku !== undefined) {
                  onOpenSku(owner.sku);
                }
              }}
              canCreate={canCreateBarcode}
              canDelete={canDeleteBarcode}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="components" className="mt-4">
          {detail !== undefined ? (
            <ComponentsPanel
              product={detail}
              components={components}
              allVariants={variantOptions}
              uoms={uoms}
              uomChoices={activeUomsQuery.data}
              onChanged={invalidateComponents}
              onOpenSku={onOpenSku}
              onOpenUomPair={onOpenUomPair}
              canCreate={canCreateComponent}
              canDelete={canDeleteComponent}
            />
          ) : null}
        </TabsContent>
      </Tabs>

      <ConflictDialog
        open={conflict}
        onReload={() => void reloadAfterConflict()}
        onDismiss={dismissConflict}
      />
    </div>
  );
}
